import * as amplitude from '@amplitude/analytics-browser';
import { SpoDeleteSportNatureResponse } from '@spo/types';
import { VpDeleteBinIcon, VpMaximizeIcon } from '@vtmn-play/icons/react';
import { VpIconButton, VpLoader } from '@vtmn-play/react';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRoles } from '../../../contexts/RolesContext';
import { useDeleteSportNature } from '../../../hooks/mutations/useDeleteSportNature';
import ArchiRowActionsModal from '../archiRowActionsModal/ArchiRowActionsModal';
import './archi-row-actions.scss';

export type ArchiModalType = 'deletion' | 'validation' | 'error' | null;

export const ArchiRowActions = ({
  natureId,
  enabled,
  deletable,
  setDrawerNatureId,
  removeDeletedNature,
}: {
  natureId: number;
  enabled: boolean;
  deletable: boolean;
  setDrawerNatureId: (natureId: string) => void;
  removeDeletedNature: (natureId: string) => void;
}) => {
  const [displayModalType, setDisplayModalType] =
    useState<ArchiModalType>(null);
  const { t } = useTranslation();
  const { hasRoleSportLeader } = useRoles();

  const params = new URLSearchParams(document.location.search);
  const sportId = params.get('sportId') || '';
  const navigate = useNavigate();

  const mutationSuccess = (response: SpoDeleteSportNatureResponse) => {
    response.status === 'success'
      ? setDisplayModalType('validation')
      : setDisplayModalType('error');
  };

  const { mutate, isPending } = useDeleteSportNature(
    {
      sportId,
      natureId: natureId.toString(),
      language: 'EN',
    },
    mutationSuccess,
  );

  const handleTrash = () => {
    if (enabled) {
      return;
    } else if (!enabled && deletable && sportId) {
      mutate();
    } else if (!enabled && !deletable) {
      setDisplayModalType('deletion');
    }
  };

  const handleCloseModal = () => {
    displayModalType === 'validation' &&
      removeDeletedNature(natureId.toString());
    setDisplayModalType(null);
  };

  const openNatureDrawer = () => {
    const natureIdParam = natureId.toString();

    const params = new URLSearchParams();
    params.set('sportId', sportId);
    params.set('natureId', natureIdParam);
    setDrawerNatureId(natureIdParam);
    amplitude.track('Architecture_Open-Nature-Drawer', {
      natureId: natureId,
    });
    navigate(`/personal-offer/architecture?${params}`);
  };

  return (
    <>
      {displayModalType && (
        <ArchiRowActionsModal
          modalType={displayModalType}
          setDisplayModalType={setDisplayModalType}
          handleCloseModal={handleCloseModal}
        />
      )}
      {isPending ? (
        <VpLoader size="small" data-testid="archi-row-enable-loader" />
      ) : (
        hasRoleSportLeader && (
          <VpIconButton
            className={clsx('archi-delete-nature-button', {
              'not-deletable': enabled,
            })}
            disabled={enabled}
            variant="tertiary"
            size="small"
            aria-label="Delete nature"
            data-testid={`delete-nature-button-${natureId}`}
            onClick={handleTrash}
          >
            <div className="deletion-tooltip">
              {t('architecture.bin-tooltip')}
            </div>
            <VpDeleteBinIcon />
          </VpIconButton>
        )
      )}
      <VpIconButton
        data-testid={`archi-row-open-drawer-button-${natureId}`}
        onClick={openNatureDrawer}
        variant="tertiary"
        size="small"
        aria-label="Open nature drawer"
      >
        <VpMaximizeIcon />
      </VpIconButton>
    </>
  );
};
